import React, { useState } from "react";
import { useFetch } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import TokenTable from "@/components/TokenTable";
import { Radar, Wifi, WifiOff, AlertTriangle } from "lucide-react";

const PRESETS = [
  ["", "All"],
  ["fresh", "Fresh"],
  ["early_momentum", "Early Momentum"],
  ["smart_wallet", "Smart Wallet Activity"],
  ["organic", "Organic Growth"],
  ["narrative_p", "Narrative"],
  ["high_liquidity", "High Liquidity"],
  ["low_risk", "Low Risk"],
];

const SORTS = [
  ["newest", "Newest"],
  ["momentum", "Momentum"],
  ["organic", "Organic"],
  ["volume", "Volume"],
  ["buyer_accel", "Buyer Accel"],
  ["holder_growth", "Holder Growth"],
  ["liquidity", "Liquidity"],
  ["market_cap", "Market Cap"],
  ["risk", "Risk"],
  ["tracked_wallets", "Tracked Wallets"],
];

export default function NewPairs() {
  const [source, setSource] = useState("demo");
  const [preset, setPreset] = useState("");
  const [sort, setSort] = useState("newest");
  const [maxRisk, setMaxRisk] = useState(100);
  const [minMomentum, setMinMomentum] = useState(0);
  const [minTracked, setMinTracked] = useState(0);

  const demoQs = `/tokens?sort=${sort}&preset=${preset}&max_risk=${maxRisk}&min_momentum=${minMomentum}&min_tracked=${minTracked}&limit=100`;
  const demo = useFetch(demoQs, [sort, preset, maxRisk, minMomentum, minTracked]);
  const live = useFetch(`/live/new-pairs?sort=${sort === "newest" ? "newest" : sort}&limit=100`, [sort, source]);

  const isLive = source === "live";
  const liveReady = isLive && live.data?.ready && (live.data?.count || 0) > 0;
  const data = liveReady ? live.data : demo.data;
  const loading = isLive ? live.loading : demo.loading;

  return (
    <div className="fade-in">
      <PageHeader
        icon={Radar}
        title="New Pairs Scanner"
        subtitle="Dense professional scanner for newly launched Solana tokens. Filter, sort and drill into any row."
        actions={
          <div className="flex items-center gap-1 rounded-lg border border-slate-800 bg-panel p-1">
            <button data-testid="source-demo" onClick={() => setSource("demo")} className={`rounded-md px-3 py-1.5 text-xs font-medium ${!isLive ? "bg-amber-500/15 text-amber-300" : "text-slate-400"}`}>Demo</button>
            <button data-testid="source-live" onClick={() => setSource("live")} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium ${isLive ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400"}`}>
              {liveReady ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />} Live · DexScreener
            </button>
          </div>
        }
      />

      {isLive && !liveReady && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-700/40 bg-amber-950/20 px-4 py-3 text-xs text-amber-300" data-testid="live-fallback-banner">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <div className="font-semibold">Live DexScreener feed is temporarily unavailable</div>
            <div className="mt-0.5 text-amber-200/70">
              DexScreener's provider is rate-limiting this server's shared IP (HTTP 1015). The backend keeps retrying every ~75s.
              Showing clearly-labeled DEMO data below in the meantime — this is not presented as live blockchain data.
              {live.data?.error ? ` (${live.data.error})` : ""}
            </div>
          </div>
        </div>
      )}

      {liveReady && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-800/40 bg-emerald-950/20 px-4 py-2.5 text-xs text-emerald-300">
          <Wifi className="h-4 w-4" /> Live DexScreener data · {live.data.count} Solana pairs · updated {live.data.last_updated ? new Date(live.data.last_updated).toLocaleTimeString() : "—"}. Market fields are LIVE; deep wallet/dev/holder intelligence is DEMO until on-chain providers connect.
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        {PRESETS.map(([v, l]) => (
          <button
            key={v || "all"}
            data-testid={`preset-${v || "all"}`}
            onClick={() => setPreset(v)}
            disabled={isLive}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-40 ${
              preset === v
                ? "border-amber-600/50 bg-amber-500/10 text-amber-300"
                : "border-slate-800 bg-panel text-slate-400 hover:border-slate-700"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="pnut-card mb-4 flex flex-wrap items-center gap-5 p-4">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase text-slate-500">Sort</span>
          <select
            data-testid="newpairs-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="rounded-md border border-slate-800 bg-void px-2 py-1 text-xs text-slate-300 outline-none"
          >
            {SORTS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        {!isLive && <Slider label={`Max Risk ${maxRisk}`} value={maxRisk} min={0} max={100} onChange={setMaxRisk} testid="filter-maxrisk" />}
        {!isLive && <Slider label={`Min Momentum ${minMomentum}`} value={minMomentum} min={0} max={100} onChange={setMinMomentum} testid="filter-minmom" />}
        {!isLive && <Slider label={`Min Tracked Wallets ${minTracked}`} value={minTracked} min={0} max={6} onChange={setMinTracked} testid="filter-mintracked" />}
        {data && <span className="ml-auto font-mono text-xs text-slate-500">{data.count} matches</span>}
      </div>

      {loading || !data ? <Loading /> : <TokenTable tokens={data.data} />}
    </div>
  );
}

function Slider({ label, value, min, max, onChange, testid }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-36 font-mono text-[10px] uppercase text-slate-500">{label}</span>
      <input
        data-testid={testid}
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-28 accent-amber-500"
      />
    </div>
  );
}
